import React, {
    createContext,
    ReactNode,
    useState,
    useMemo,
    useCallback,
    useEffect,
} from 'react';
import { User as UserFields } from '@/generated/graphql';
import { checkLowBatteryStatus, getDevicePerformanceProfile } from '@/utils';

interface AuxProps {
    children: ReactNode;
}

export enum EditModeComponent {
    Cluster = 'CLUSTER',
    Matrix = 'MATRIX',
    Media = 'MEDIA',
    Panel = 'PANEL',
    Realm = 'REALM',
    Zone = 'ZONE',
}

export interface DeviceProfile {
    deviceSpeed: 1 | 2 | 3;
    deviceMemory: 1 | 2 | 3;
    networkSpeed: 1 | 2 | 3;
    lowBattery: boolean;
    devicePixelRatio: number;
    hasTrueHover: boolean;
}

export interface UserCurrentFields extends Partial<UserFields> {
    id?: number;
    isAnonymous?: boolean; // New flag for unauthenticated traffic
    isContentEditor?: boolean;
    isEditMode?: EditModeComponent | boolean | undefined;
    deviceProfile?: DeviceProfile;
}

export interface UserCurrentContextType {
    userCurrent: UserCurrentFields; // Never null anymore
    setUserCurrent: React.Dispatch<
        React.SetStateAction<UserCurrentFields | null>
    >;
    refreshBattery: () => Promise<void>;
}

export const UserCurrentContext = createContext<UserCurrentContextType | null>(
    null,
);
const { Provider } = UserCurrentContext;

const UserCurrentProvider = ({ children }: AuxProps) => {
    const [userCurrent, setUserCurrent] = useState<UserCurrentFields | null>(
        null,
    );
    const [deviceProfile, setDeviceProfile] = useState<DeviceProfile | null>(
        null,
    );

    // 1. Gather hardware footprints instantly on mount
    useEffect(() => {
        const metrics = getDevicePerformanceProfile();
        setDeviceProfile({ ...metrics, lowBattery: false });
    }, []);

    // 2. Centrally manage modern async battery updates
    const refreshBattery = useCallback(async () => {
        const isLow = await checkLowBatteryStatus();
        setDeviceProfile((prev) => {
            if (!prev || prev.lowBattery === isLow) return prev;
            return { ...prev, lowBattery: isLow };
        });
    }, []);

    // 3. Structural Engine: Blend Auth details or build Guest properties cleanly
    const unifiedUser = useMemo((): UserCurrentFields => {
        const metrics = deviceProfile || {
            deviceSpeed: 2,
            deviceMemory: 2,
            networkSpeed: 2,
            lowBattery: false,
            devicePixelRatio: 1,
            hasTrueHover: true,
        };

        if (!userCurrent) {
            // Unauthenticated Guest Baseline Object
            return {
                isAnonymous: true,
                deviceProfile: metrics,
            };
        }

        // Authenticated Session Object Layering
        return {
            ...userCurrent,
            isAnonymous: false,
            deviceProfile: metrics,
        };
    }, [userCurrent, deviceProfile]);

    const contextValue = useMemo(
        () => ({
            userCurrent: unifiedUser,
            setUserCurrent,
            refreshBattery,
        }),
        [unifiedUser, refreshBattery],
    );

    return <Provider value={contextValue}>{children}</Provider>;
};

UserCurrentProvider.context = UserCurrentContext;

export const useUserCurrent = () => {
    const context = React.useContext(UserCurrentContext);
    if (!context)
        throw new Error(
            'useUserCurrent must be used within a UserCurrentProvider',
        );
    return context;
};

export default UserCurrentProvider;
